import { TooltipV2 } from 'builder/components/Tooltip'
import { useMediaQueries } from 'builder/hooks/useMediaQueries'
import { HorizontalSlider } from 'builder/components/HorizontalSlider'
import { SliderContainer, ValueContainer, Value } from './styles'

type Props = {
  value: number
  onChange?: (value: number) => void
  disabledTooltip?: string
  isDisabled?: boolean
  testId?: string
  readableDefaultValue?: number
  readableStep?: number
  readableUnit?: string
  readableMin?: number
  readableMax?: number
  isIntermediateDotsDisabled?: boolean
}

/**
 * Relative means that the value is returned in a range [-1, 1] where 0 is the default value
 */
export const GenericRelativeSlider = (props: Props) => {
  const {
    value,
    onChange,
    disabledTooltip,
    isDisabled,
    testId,
    readableUnit = '%',
    readableDefaultValue = 100,
    readableMin = 50,
    readableMax = 150,
    readableStep = 10,
    isIntermediateDotsDisabled,
  } = props
  const { isAboveTablet } = useMediaQueries()
  const readableMagnitude = Math.max(
    Math.abs(readableMin - readableDefaultValue),
    Math.abs(readableMax - readableDefaultValue),
  )

  const prettify = (value: number) =>
    `${readableDefaultValue + Math.round(readableMagnitude * value)}${readableUnit || ''}`

  const convertToRelative = (val: number) => {
    if (readableDefaultValue === readableMin) {
      console.error('readableDefaultValue === readableMin, which leads to division by zero')
      return 0
    }

    if (val <= readableDefaultValue) {
      return (val - readableDefaultValue) / (readableDefaultValue - readableMin)
    } else {
      return (val - readableDefaultValue) / (readableMax - readableDefaultValue)
    }
  }

  const step = convertToRelative(readableDefaultValue + readableStep)
  const min = convertToRelative(readableMin)
  const max = convertToRelative(readableMax)
  const prettifiedValue = prettify(value)

  return (
    <TooltipV2
      value={disabledTooltip}
      width={250}
      multiline={true}
      position="bottom"
      isActive={isDisabled}
    >
      <SliderContainer
        data-testid={testId}
        data-value={value}
        data-value-prettified={prettifiedValue}
      >
        {!isAboveTablet && (
          <ValueContainer>
            <Value>{prettify(min)}</Value>
          </ValueContainer>
        )}
        <HorizontalSlider
          disabled={!!isDisabled}
          defaultValue={0}
          value={value}
          step={step}
          min={min}
          max={max}
          onChange={onChange}
          isIntermediateDotsDisabled={isIntermediateDotsDisabled}
        />
        {!isAboveTablet && (
          <ValueContainer>
            <Value>{prettify(max)}</Value>
          </ValueContainer>
        )}
        {isAboveTablet && (
          <ValueContainer>
            <Value>{prettifiedValue}</Value>
          </ValueContainer>
        )}
      </SliderContainer>
    </TooltipV2>
  )
}
